import _ from 'lodash';
import { FC, useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  InfoIcon,
  Text,
  TextButton,
  TextProps,
} from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import { MultiFilterSelections } from '../MultiSelectionFilter';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';

export const dataHooks = {
  container: 'templates-filter-multi-selection-filter',
  checkbox: (prefix: string, selectionId: string) =>
    `${prefix}-${selectionId}-checkbox`,
  showMoreButton: 'templates-filter-multi-selection-show-more',
};

interface IControlledCheckboxesProps {
  selections: MultiFilterSelections;
  onChange: (eventData: { id: string; checked: boolean }) => void;
  langTokenPrefix: string;
  gapSize?: string;
  textSize?: TextProps['size'];
  dataHook?: string;
  useShowMore?: boolean;
}

const ControlledCheckboxGroup: FC<IControlledCheckboxesProps> = ({
  selections,
  onChange,
  langTokenPrefix,
  gapSize,
  textSize,
  dataHook = '',
  useShowMore,
}) => {
  const { t, i18n } = useTranslation();
  const { logBiClick } = useTemplatesBi({ is_mobile: false });

  const {
    displayedSelections,
    isShowingAll,
    toggleShowMore,
    shouldDisplayShowMoreButton,
  } = useShowMoreButton(selections, useShowMore);

  return (
    <Box
      direction="vertical"
      gap={gapSize ? gapSize : '12px'}
      dataHook={dataHooks.container}
    >
      {_.map(displayedSelections, (selection) => (
        <Checkbox
          key={selection.id}
          dataHook={dataHooks.checkbox(dataHook, selection.id)}
          id={`${selection.id}_${dataHook}`}
          size="small"
          checked={selection.isChecked}
          onChange={(e) =>
            onChange({ id: selection.id, checked: e.target.checked })
          }
        >
          <Box verticalAlign="middle" gap={'6px'}>
            <Text size={textSize}>
              {t(`${langTokenPrefix}${selection.label}`)}
            </Text>
            {selection.showInfoIcon && (
              <InfoIcon
                dataHook={`multi-selection-info-icon-${selection.label}`}
                size="small"
                tooltipProps={{ maxWidth: '240px' }}
                content={
                  <Box direction="vertical" gap="6px">
                    <Text light size="small">
                      {t(
                        `marketplace.templates-page.feature-${selection.label}-more-info`,
                      )}
                    </Text>
                    <TextButton
                      size="small"
                      underline="always"
                      skin="light"
                      onClick={() => {
                        logBiClick({
                          click_type: `${selection.label} - Learn more`,
                        });
                        window.open(
                          i18n.t(
                            `marketplace.templates-page.feature-${selection.label}.link`,
                          ),
                          '_blank',
                        );
                      }}
                    >
                      {t(
                        `marketplace.templates-page.feature-more-info-learn-more`,
                      )}
                    </TextButton>
                  </Box>
                }
              />
            )}
          </Box>
        </Checkbox>
      ))}
      {shouldDisplayShowMoreButton && (
        <ShowMoreTextButton
          onClick={toggleShowMore}
          isShowingMore={isShowingAll}
        />
      )}
    </Box>
  );
};

export default ControlledCheckboxGroup;

interface IShowMoreButtonProps {
  isShowingMore: boolean;
  onClick: () => void;
}

export const MAX_INITIAL_DISPLAY_COUNT = 10;
const useShowMoreButton = (
  selections: MultiFilterSelections,
  useShowMore?: boolean,
) => {
  const shouldDisplayShowMoreButton =
    Boolean(useShowMore) && selections.length > MAX_INITIAL_DISPLAY_COUNT;

  const [isShowingAll, setIsShowingAll] = useState(true);

  const displayedSelections = isShowingAll
    ? selections
    : selections.slice(0, MAX_INITIAL_DISPLAY_COUNT);

  const toggleShowMore = () => setIsShowingAll((current) => !current);

  useEffect(() => {
    setIsShowingAll(!shouldDisplayShowMoreButton);
  }, [useShowMore, selections.length]);

  return {
    displayedSelections,
    isShowingAll,
    toggleShowMore,
    shouldDisplayShowMoreButton,
  };
};

const ShowMoreTextButton: FC<IShowMoreButtonProps> = ({
  onClick,
  isShowingMore,
}) => {
  const { t } = useTranslation();
  return (
    <TextButton
      underline="always"
      skin="dark"
      onClick={onClick}
      dataHook={dataHooks.showMoreButton}
    >
      {t(isShowingMore ? 'show-less-text-button' : 'show-more-text-button')}
    </TextButton>
  );
};
